/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IAppState,
  IMediaModel,
  LabelHelper,
  LabelPlacement,
  TransmissionLabelFormatVariant,
  useTheme,
} from "@bms/common";
import cx from "classnames";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import "./MediaTransmissionStatusLabel.scss";

export interface IMediaTransmissionStatusLabelProps {
  media?: IMediaModel;
  placement?: LabelPlacement;
  formatVariant?: TransmissionLabelFormatVariant;
}

export const MediaTransmissionStatusLabel = ({
  media,
  placement = LabelPlacement.None,
  formatVariant = TransmissionLabelFormatVariant.DateTime,
}: IMediaTransmissionStatusLabelProps) => {
  const { t } = useTranslation();
  const { themeProvider } = useTheme();
  const configurationVersion = useSelector(
    (state: IAppState) => state.configuration.configuration?.VersionNumber,
  );

  const branding = useMemo(() => {
    void configurationVersion;
    return themeProvider.getBranding();
  }, [configurationVersion, themeProvider]);

  const formatConfig = useMemo(
    () => LabelHelper.getTransmissionFormatConfig(branding),
    [branding],
  );

  const edgePaddingEnabled = useMemo(
    () => LabelHelper.isEdgePaddingEnabled(branding),
    [branding],
  );

  const labelStyle = useMemo<React.CSSProperties>(() => {
    const edgeOffset = "var(--app-label-edge-offset, 0.25rem)";

    switch (placement) {
      case LabelPlacement.TopLeft:
        return {
          top: edgeOffset,
          left: edgeOffset,
        };
      case LabelPlacement.TopRight:
        return {
          top: edgeOffset,
          right: edgeOffset,
        };
      case LabelPlacement.BottomLeft:
        return {
          bottom: edgeOffset,
          left: edgeOffset,
        };
      case LabelPlacement.BottomRight:
        return {
          bottom: edgeOffset,
          right: edgeOffset,
        };
      case LabelPlacement.Metadata:
        return {
          position: "relative",
        };
      default:
        return {
          display: "none",
        };
    }
  }, [placement]);

  const transitionStatus = LabelHelper.getMediaTransmissionStatusLabel(media, {
    formatConfig,
    formatVariant,
    endedLabel: t("MEDIA_TRANSMISSION_STATUS_ENDED"),
    liveLabel: t("MEDIA_TRANSMISSION_STATUS_LIVE"),
    todayLabel: t("MEDIA_TRANSMISSION_STATUS_TODAY"),
  });

  if (!transitionStatus) {
    return null;
  }

  const labelPlacement = LabelHelper.getLabelPlacementAttribute(placement);

  return (
    <div
      className={cx("MediaTransmissionStatusLabel", {
        "MediaTransmissionStatusLabel--no-edge-padding": !edgePaddingEnabled,
      })}
      style={labelStyle}
      data-transmission-status={transitionStatus.transmissionStatus}
      data-label-placement={labelPlacement}
    >
      <span>{transitionStatus.label}</span>
    </div>
  );
};
